/**
 * Canvas abstraction that works identically on the main thread
 * (HTMLCanvasElement) and inside a Web Worker (OffscreenCanvas).
 */

export type AnyCanvas = HTMLCanvasElement | OffscreenCanvas;
export type AnyCtx =
  | CanvasRenderingContext2D
  | OffscreenCanvasRenderingContext2D;

export function hasOffscreen(): boolean {
  return typeof OffscreenCanvas !== "undefined";
}

export function makeCanvas(w: number, h: number): AnyCanvas {
  const width = Math.max(1, Math.round(w));
  const height = Math.max(1, Math.round(h));
  if (typeof document !== "undefined") {
    const c = document.createElement("canvas");
    c.width = width;
    c.height = height;
    return c;
  }
  return new OffscreenCanvas(width, height);
}

export function ctx2d(c: AnyCanvas): AnyCtx {
  const ctx = (c as HTMLCanvasElement).getContext("2d", {
    willReadFrequently: false,
  }) as AnyCtx | null;
  if (!ctx) throw new Error("Canvas 2D context unavailable");
  return ctx;
}

/** Encode a canvas to a Blob, preferring the highest-quality codec available. */
export async function encodeCanvas(
  c: AnyCanvas,
  type: string,
  quality: number,
): Promise<Blob> {
  if ("convertToBlob" in c) {
    return await (c as OffscreenCanvas).convertToBlob({ type, quality });
  }
  return await new Promise<Blob>((resolve, reject) => {
    (c as HTMLCanvasElement).toBlob(
      (b) => (b ? resolve(b) : reject(new Error("Canvas encode failed"))),
      type,
      quality,
    );
  });
}

/** True when the runtime can encode the given mime type. */
export async function supportsType(type: string): Promise<boolean> {
  try {
    const c = makeCanvas(2, 2);
    const b = await encodeCanvas(c, type, 0.8);
    return b.type === type;
  } catch {
    return false;
  }
}

export function drawableToImageData(
  source: CanvasImageSource,
  w: number,
  h: number,
): ImageData {
  const c = makeCanvas(w, h);
  const ctx = ctx2d(c);
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = "high";
  ctx.drawImage(source, 0, 0, w, h);
  return ctx.getImageData(0, 0, w, h);
}

export function imageDataToCanvas(data: ImageData): AnyCanvas {
  const c = makeCanvas(data.width, data.height);
  ctx2d(c).putImageData(data, 0, 0);
  return c;
}
